import { Link } from "react-router-dom";
import {
  HeartHandshake,
  Home as HomeIcon,
  Pill,
  Coffee,
  Brain,
  Sunset,
  Stethoscope,
  Sparkles,
  ArrowRight,
  Phone,
} from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SITE, SERVICES } from "@/lib/site";
import { SERVICES_PAGE } from "@/constants/testIds";

const ICONS = {
  HeartHandshake,
  Home: HomeIcon,
  Pill,
  Coffee,
  Brain,
  Sunset,
  Stethoscope,
  Sparkles,
};

export default function Services() {
  return (
    <div data-testid={SERVICES_PAGE.root}>
      <PageHero
        eyebrow="Our services"
        title={
          <>
            Care that's{" "}
            <em className="not-italic text-brand">designed around you</em>.
          </>
        }
        intro="Every service is built on the same foundation — listening, understanding and treating each person as an individual. Below is an overview of the support we deliver across Doncaster and Leicester."
        image={SITE.imagery.servicesOverview}
        imageAlt="Carer assisting an elderly woman at home"
      />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => {
            const Cmp = ICONS[s.icon] || Sparkles;
            return (
              <Link
                to={`/services/${s.slug}`}
                key={s.slug}
                data-testid={SERVICES_PAGE.card(s.slug)}
                className="group relative bg-white border border-brand-line rounded-3xl p-8 lg:p-10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-brand-soft flex items-center justify-center shrink-0 group-hover:bg-brand transition-colors duration-300">
                    <Cmp
                      className="w-7 h-7 text-brand group-hover:text-white transition-colors duration-300"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-4">
                      <h2 className="font-display text-2xl lg:text-3xl text-brand-ink">
                        {s.title}
                      </h2>
                      <span className="text-xs font-mono text-brand-mute/60">
                        0{i + 1}
                      </span>
                    </div>
                    <p className="mt-4 text-brand-mute leading-relaxed">
                      {s.summary}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand group-hover:gap-2.5 transition-all">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-brand-cream border border-brand-line rounded-[2rem] p-10 lg:p-16 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-soft blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Not sure which service is right?{" "}
                  <em className="not-italic text-brand">Let's talk.</em>
                </h2>
                <p className="mt-5 text-lg text-brand-mute max-w-2xl">
                  Every care package begins with a friendly conversation and a
                  free assessment. We'll help you understand the options and
                  design something that fits.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <a
                  href={SITE.phoneHref}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-base font-semibold hover:bg-brand-hover transition-all duration-300 hover:-translate-y-0.5 w-full lg:w-auto"
                >
                  <Phone className="w-4 h-4" /> {SITE.phone}
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/20 bg-white text-brand-ink px-7 py-4 text-base font-semibold hover:bg-brand-soft transition w-full lg:w-auto"
                >
                  Contact us <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
